// Secondary trip-tab panels: Ideas (browse/select/edit ideas), Presets
// (destination day-preset blocks), and Dates. Rendered by ItineraryScreen's
// tab switch; all state flows down from App.js via ItineraryScreen.
import React from 'react';
import { PALETTE } from '../../theme';
import { Icon, Chip, Patch } from '../../components/UI';
import { IDEA_CATS, SWATCH_BY_CAT, getPresetsForDest } from '../../data';
import { CUSTOM_CATS } from './sheets';
import { IdeaBar } from '../../components/IdeaBar';
import { CalPicker, addDaysIso, MultiCityStops } from '../NewTripScreen';
import { computeLegSchedule } from '../../utils/legSchedule';
import { isSafeHttpUrl } from '../../utils/url';
import { useAuth } from '../../contexts/AuthContext';
import { uniqueHotelNames, buildRestaurantQuery } from '../../utils/restaurantSearch';

function IdeaEditForm({ idea, accent, onSave, onCancel, legs = null }) {
  const [title, setTitle] = React.useState(idea.title);
  const [cat, setCat] = React.useState(idea.cat || 'Experience');
  const [dur, setDur] = React.useState(idea.dur || 1);
  const [area, setArea] = React.useState(idea.area || '');
  const [notes, setNotes] = React.useState(idea.meta || '');
  const [requiresBooking, setRequiresBooking] = React.useState(!!idea.requiresBooking);
  // Collapsed by default — same reasoning as IdeaBar's own expand toggle:
  // category/section/duration/notes/booking is a lot to show just to fix a
  // typo in the title. Existing values still load into state above so a
  // Save without expanding leaves them untouched.
  const [expanded, setExpanded] = React.useState(false);
  const isMultiCity = legs && legs.length > 1;
  const cityOf = (i) => (legs?.[i]?.destination || '').split(',')[0].trim();
  // Untagged (no legIndex) starts the "Section" picker on Untagged, not leg
  // 0 — this form must not silently invent a city for an idea that doesn't
  // have one when the user just meant to edit its title/duration/etc.
  const initialCityIdx = isMultiCity && idea.legIndex != null
    ? (idea.destCity ? legs.findIndex((l, i) => cityOf(i) === idea.destCity) : idea.legIndex)
    : null;
  const [cityIdx, setCityIdx] = React.useState(initialCityIdx != null && initialCityIdx >= 0 ? initialCityIdx : null);

  const save = () => {
    if (!title.trim()) return;
    const base = { ...idea, title: title.trim(), cat, dur, area, meta: notes, requiresBooking };
    if (isMultiCity && cityIdx != null) {
      onSave({ ...base, legIndex: cityIdx, destCity: cityOf(cityIdx) });
    } else {
      const { legIndex, destCity, ...untagged } = base;
      onSave(untagged);
    }
  };

  const fieldInput = { width: '100%', boxSizing: 'border-box', appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, padding: '7px 12px', fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, background: PALETTE.paper, outline: 'none' };

  return (
    <div style={{ padding: 14, background: PALETTE.paperDim, borderRadius: 12, border: `1px solid ${PALETTE.rule}`, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }}
          placeholder="Title"
          style={{ ...fieldInput, flex: 1 }}
        />
        {/* Save/Cancel as icon buttons in the same row as the title — a
            full-width Save/Cancel row below just to confirm a title edit
            was one line of chrome too many. */}
        <button onClick={save} aria-label="Save" title="Save" style={{ appearance: 'none', border: 0, cursor: 'pointer', background: accent, color: '#fff', borderRadius: 6, padding: 6, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Icon name="check" size={14} stroke={2.5} />
        </button>
        <button onClick={onCancel} aria-label="Cancel" title="Cancel" style={{ appearance: 'none', border: 0, cursor: 'pointer', background: 'transparent', color: PALETTE.inkMute, borderRadius: 6, padding: 6, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Icon name="x" size={14} />
        </button>
        <button
          onClick={() => setExpanded(e => !e)}
          aria-label={expanded ? 'Collapse idea options' : 'Expand idea options'}
          aria-expanded={expanded}
          style={{ appearance: 'none', border: 0, background: 'transparent', color: PALETTE.inkMute, cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={13} />
        </button>
      </div>
      {expanded && (
        <>
          {isMultiCity && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute }}>Section</span>
              <Chip active={cityIdx == null} onClick={() => setCityIdx(null)} style={{ padding: '3px 10px', fontSize: 11 }}>Untagged</Chip>
              {legs.map((l, i) => (
                <Chip key={i} active={cityIdx === i} onClick={() => setCityIdx(i)} style={{ padding: '3px 10px', fontSize: 11 }}>{cityOf(i)}</Chip>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {CUSTOM_CATS.map(c => (
              <button key={c} onClick={() => setCat(c)} style={{
                appearance: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: 999,
                background: cat === c ? PALETTE.ink : 'transparent',
                color: cat === c ? PALETTE.paper : PALETTE.inkSoft,
                fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
                border: cat === c ? 'none' : `1px solid ${PALETTE.rule}`,
              }}>{c}</button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute, minWidth: 56 }}>Duration</span>
            <button onClick={() => setDur(d => Math.max(0.5, +(d - 0.5).toFixed(1)))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, width: 26, height: 26, background: PALETTE.paper, cursor: 'pointer', color: PALETTE.ink, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
            <span style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, minWidth: 32, textAlign: 'center' }}>{dur < 1 ? `${dur * 60}m` : `${dur}h`}</span>
            <button onClick={() => setDur(d => Math.min(12, +(d + 0.5).toFixed(1)))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 6, width: 26, height: 26, background: PALETTE.paper, cursor: 'pointer', color: PALETTE.ink, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
          </div>
          <input value={area} onChange={e => setArea(e.target.value)} placeholder="Location / area (optional)" style={fieldInput} />
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)" style={fieldInput} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: PALETTE.inkMute }}>Require booking</span>
            <button onClick={() => setRequiresBooking(r => !r)} style={{
              appearance: 'none', cursor: 'pointer', padding: '4px 12px', borderRadius: 999,
              background: requiresBooking ? accent : 'transparent',
              color: requiresBooking ? '#fff' : PALETTE.inkSoft,
              fontFamily: 'Inter', fontSize: 11, fontWeight: 500,
              border: requiresBooking ? 'none' : `1px solid ${PALETTE.rule}`,
            }}>{requiresBooking ? 'Yes — needs booking' : 'No'}</button>
          </div>
          {isSafeHttpUrl(idea.sourceUrl) && (
            <a href={idea.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, wordBreak: 'break-all', textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 4 }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M7 1h2v2M9 1L5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg><span style={{ opacity: 0.7 }}>{idea.sourceUrl}</span>
            </a>
          )}
        </>
      )}
    </div>
  );
}

function InspirationSheet({ ideas, accent, onAdd, onAddCustom, onClose, legs = null, bookings = {}, destination = '' }) {
  const { user } = useAuth();
  const [cat, setCat] = React.useState('All');
  const [city, setCity] = React.useState('All');
  const [search, setSearch] = React.useState('');
  const isMultiCity = legs && legs.length > 1;
  // Trip-leg order, not alphabetical — same reasoning as cityGroups in
  // IdeasPanel below.
  const allCities = isMultiCity ? ['All', ...new Set(legs.map(l => (l.destination || '').split(',')[0].trim()))] : null;
  const allCats = ['All', ...IDEA_CATS.filter(c => c !== 'All')];
  const cityFiltered = (isMultiCity && city !== 'All') ? ideas.filter(i => i.destCity === city) : ideas;
  const catFiltered = cat === 'All' ? cityFiltered : cityFiltered.filter(i => i.cat === cat);
  const filtered = search.trim()
    ? catFiltered.filter(i => i.title.toLowerCase().includes(search.trim().toLowerCase()))
    : catFiltered;

  // Restaurant search — a separate "mode" layered on top of the seed-idea
  // browse list above. restaurantMode holds {label, params, cityIdx} once a
  // quick-action chip is tapped; null means "browsing seed ideas as normal".
  const [restaurantMode, setRestaurantMode] = React.useState(null);
  const [restaurantResults, setRestaurantResults] = React.useState([]);
  const [restaurantLoading, setRestaurantLoading] = React.useState(false);
  const [restaurantError, setRestaurantError] = React.useState('');

  const hotelNames = React.useMemo(() => uniqueHotelNames(bookings), [bookings]);
  // Only offer "in {city}" once a specific city is resolvable: the trip's own
  // destination for single-city trips, or the active city chip (not "All")
  // for multi-city ones — searching restaurants "in All" isn't a real query.
  const restaurantCity = isMultiCity
    ? (city !== 'All' ? city : null)
    : ((destination || '').split(',')[0].trim() || null);
  const restaurantCityIdx = isMultiCity && restaurantCity ? legs.findIndex((l, i) => (l.destination || '').split(',')[0].trim() === restaurantCity) : null;

  const runRestaurantSearch = React.useCallback(async (params, label, cityIdx = null) => {
    setRestaurantMode({ label, params, cityIdx });
    setRestaurantLoading(true);
    setRestaurantError('');
    setRestaurantResults([]);
    try {
      const token = user ? await user.getIdToken() : '';
      const res = await fetch('/api/tp/ai/restaurants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ query: buildRestaurantQuery(params) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not find restaurants');
      setRestaurantResults(data.items || []);
    } catch (err) { setRestaurantError(err.message); }
    finally { setRestaurantLoading(false); }
  }, [user]);

  const addRestaurantResult = (item, idx) => {
    const cityTag = isMultiCity && restaurantMode?.cityIdx != null && restaurantMode.cityIdx >= 0
      ? { legIndex: restaurantMode.cityIdx, destCity: restaurantCity }
      : {};
    onAddCustom?.({
      id: `restaurant_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      title: item.title, cat: 'Food', dur: 1.5, area: item.area || '', meta: item.notes || '', requiresBooking: false,
      ...cityTag,
    });
    setRestaurantResults(prev => prev.filter((_, j) => j !== idx));
  };

  const backToIdeas = () => { setRestaurantMode(null); setRestaurantResults([]); setRestaurantError(''); };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(34,31,26,0.55)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: PALETTE.paper, borderRadius: '20px 20px 0 0', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -12px 48px rgba(34,31,26,0.18)' }}>
        {/* drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 2px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: PALETTE.rule }} />
        </div>
        {/* header */}
        <div style={{ padding: '10px 20px 14px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Fraunces', fontSize: 24, color: PALETTE.ink, letterSpacing: -0.4, lineHeight: 1.1 }}>
              {restaurantMode ? restaurantMode.label : 'Get inspiration'}
            </div>
            <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, marginTop: 3 }}>
              {restaurantMode
                ? (restaurantLoading ? 'Searching…' : restaurantError ? ' ' : `${restaurantResults.length} restaurant${restaurantResults.length !== 1 ? 's' : ''} — tap to add to your itinerary`)
                : `${ideas.length} idea${ideas.length !== 1 ? 's' : ''} — tap to add to your itinerary`}
            </div>
          </div>
          <button onClick={onClose} style={{ appearance: 'none', border: 0, background: PALETTE.paperDim, color: PALETTE.ink, width: 32, height: 32, borderRadius: 999, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
            <Icon name="x" size={16} />
          </button>
        </div>

        {restaurantMode ? (
          <div style={{ padding: '0 20px 10px' }}>
            <button onClick={backToIdeas} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: accent, fontFamily: 'Inter', fontSize: 12, fontWeight: 500, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name="arrowL" size={12} /> Back to ideas
            </button>
          </div>
        ) : (
          <>
            {/* search */}
            <div style={{ padding: '0 20px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: PALETTE.card, borderRadius: 10, border: `1px solid ${PALETTE.rule}` }}>
                <Icon name="search" size={14} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" style={{ flex: 1, appearance: 'none', border: 0, outline: 'none', background: 'transparent', fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink }} />
                {search && <button onClick={() => setSearch('')} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, fontSize: 13, padding: 0 }}>✕</button>}
              </div>
            </div>
            {/* restaurant search quick actions — AI-curated, separate from
                the seed-idea catalog browsed below */}
            {(restaurantCity || hotelNames.length > 0) && (
              <div style={{ padding: '0 20px 10px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {restaurantCity && (
                  <button
                    onClick={() => runRestaurantSearch({ city: restaurantCity }, `Restaurants in ${restaurantCity}`, restaurantCityIdx)}
                    style={{ appearance: 'none', cursor: 'pointer', padding: '6px 12px', borderRadius: 999, background: 'transparent', border: `1px solid ${PALETTE.rule}`, color: PALETTE.ink, fontFamily: 'Inter', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    🍽 Best restaurants in {restaurantCity}
                  </button>
                )}
                {hotelNames.map(name => (
                  <button
                    key={name}
                    onClick={() => runRestaurantSearch({ hotelName: name }, `Restaurants near ${name}`)}
                    style={{ appearance: 'none', cursor: 'pointer', padding: '6px 12px', borderRadius: 999, background: 'transparent', border: `1px solid ${PALETTE.rule}`, color: PALETTE.ink, fontFamily: 'Inter', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    🍽 Near {name}
                  </button>
                ))}
              </div>
            )}
            {/* city chips — multi-city trips only, above category so you can
                narrow to one leg before also filtering by category */}
            {isMultiCity && (
              <div style={{ padding: '0 20px 8px' }}>
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  {allCities.map(c => <Chip key={c} active={city === c} onClick={() => setCity(c)}>{c}</Chip>)}
                </div>
              </div>
            )}
            {/* category chips */}
            <div style={{ padding: '0 20px 10px' }}>
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {allCats.map(c => <Chip key={c} active={cat === c} onClick={() => setCat(c)}>{c}</Chip>)}
              </div>
            </div>
          </>
        )}

        {/* list */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '0 20px 32px' }}>
          {restaurantMode ? (
            restaurantLoading ? (
              <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute }}>Finding restaurants…</div>
            ) : restaurantError ? (
              <div style={{ padding: '14px 0' }}>
                <div style={{ padding: '10px 12px', background: PALETTE.redSoft, borderRadius: 8, fontFamily: 'Inter', fontSize: 12, color: PALETTE.red, marginBottom: 10 }}>{restaurantError}</div>
                <button onClick={() => runRestaurantSearch(restaurantMode.params, restaurantMode.label, restaurantMode.cityIdx)} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent', color: PALETTE.ink, borderRadius: 8, padding: '6px 14px', fontFamily: 'Inter', fontSize: 12, cursor: 'pointer' }}>Try again</button>
              </div>
            ) : restaurantResults.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute }}>No restaurants found.</div>
            ) : restaurantResults.map((item, i) => (
              <button key={i} onClick={() => addRestaurantResult(item, i)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', width: '100%', appearance: 'none', cursor: 'pointer', border: 0, borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, background: 'transparent', textAlign: 'left', touchAction: 'manipulation' }}>
                <div style={{ width: 68, height: 58, borderRadius: 10, flexShrink: 0, background: SWATCH_BY_CAT.Food || PALETTE.paperDim, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 9px)' }} />
                  {item.area && <div style={{ position: 'absolute', bottom: 4, left: 6, right: 6, fontFamily: 'JetBrains Mono', fontSize: 7, color: 'rgba(255,255,255,0.9)', letterSpacing: 0.6, textTransform: 'uppercase', lineHeight: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{item.area}</div>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, lineHeight: 1.25, letterSpacing: -0.2 }}>{item.title}</div>
                  {item.notes && <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkSoft, marginTop: 3, lineHeight: 1.35 }}>{item.notes}</div>}
                </div>
                <div style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="plus" size={14} stroke={2} />
                </div>
              </button>
            ))
          ) : filtered.length === 0 ? (
            <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute }}>No ideas match.</div>
          ) : filtered.map(it => (
            <button key={it.id} onClick={() => onAdd(it.id)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '11px 0', width: '100%', appearance: 'none', cursor: 'pointer', border: 0, borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, background: 'transparent', textAlign: 'left', touchAction: 'manipulation' }}>
              <div style={{ width: 68, height: 58, borderRadius: 10, flexShrink: 0, background: SWATCH_BY_CAT[it.cat] || PALETTE.paperDim, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 9px)' }} />
                {it.area && <div style={{ position: 'absolute', bottom: 4, left: 6, right: 6, fontFamily: 'JetBrains Mono', fontSize: 7, color: 'rgba(255,255,255,0.9)', letterSpacing: 0.6, textTransform: 'uppercase', lineHeight: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{it.area}</div>}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, lineHeight: 1.25, letterSpacing: -0.2 }}>{it.title}</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.5, marginTop: 4, textTransform: 'uppercase' }}>
                  {isMultiCity && it.destCity && <span style={{ color: accent }}>{it.destCity} · </span>}
                  {it.cat} · {it.dur < 1 ? `${Math.round(it.dur * 60)}m` : `${it.dur}h`}
                </div>
              </div>
              <div style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="plus" size={14} stroke={2} />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const IDEA_SORT_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled first' },
  { value: 'unscheduled', label: 'Unscheduled first' },
  { value: 'cat', label: 'By category' },
  { value: 'dur', label: 'By duration' },
  { value: 'az', label: 'A – Z' },
];

export function IdeasPanel({ selectedIds, setSelectedIds, allIdeas, accent, px, onAddCustom, customIdeaIds = new Set(), onEditCustom, onDeleteCustom, overflowIds = new Set(), placedIdeaIds = new Set(), legs = null, bookings = {}, destination = '' }) {
  const [editingId, setEditingId] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);
  const [showInspiration, setShowInspiration] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState('scheduled');
  const [showSort, setShowSort] = React.useState(false);
  const [filterArea, setFilterArea] = React.useState(null);
  const sortRef = React.useRef(null);

  React.useEffect(() => {
    if (!showSort) return;
    const close = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setShowSort(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showSort]);

  const [filterCity, setFilterCity] = React.useState(null);
  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);
  const toggle = (id) => setSelectedIds(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  const isMultiCity = legs && legs.length > 1;

  // Single-city trips filter "My ideas" by neighborhood/area (curated data
  // tags ideas with real place names there). Multi-city trips filter by the
  // trip's own leg cities instead — area chips there would show whatever
  // random neighborhood names each leg's curated (or generic-fallback) idea
  // bucket happens to use, which have nothing to do with the trip's actual
  // cities. See cityOptions below for the multi-city case.
  const areas = React.useMemo(() => {
    if (isMultiCity) return [];
    const seen = new Set();
    allIdeas.filter(i => selectedSet.has(i.id) && i.area).forEach(i => seen.add(i.area));
    return [...seen].sort();
  }, [allIdeas, selectedIds, isMultiCity]); // eslint-disable-line

  // Ordered like the trip's own legs — every leg gets a tag whether or not
  // an idea has been assigned to it yet (a newly added stop, e.g. a leg
  // added mid-trip, must show up as a tag immediately, not only once
  // something is tagged into it), plus "Untagged" if any selected idea has
  // no destCity (an idea added without picking a city — see
  // IdeaBar.js/App.js#ideasForTrip, which never guess a city for those).
  const cityOptions = React.useMemo(() => {
    if (!isMultiCity) return [];
    const legOrder = [...new Set(legs.map(l => (l.destination || '').split(',')[0].trim()))];
    const hasUntagged = allIdeas.some(i => selectedSet.has(i.id) && !i.destCity);
    return [...legOrder, ...(hasUntagged ? ['Untagged'] : [])];
  }, [isMultiCity, legs, allIdeas, selectedIds]); // eslint-disable-line

  // Drives the city an idea gets auto-tagged to when typed into IdeaBar
  // below: whichever city tab is active. "All cities"/"Untagged" both mean
  // "don't guess" — only a specific city tab presets a tag.
  const defaultCityIdx = React.useMemo(() => {
    if (!isMultiCity || !filterCity || filterCity === 'Untagged') return null;
    const idx = legs.findIndex(l => (l.destination || '').split(',')[0].trim() === filterCity);
    return idx >= 0 ? idx : null;
  }, [isMultiCity, filterCity, legs]);

  const myIdeas = React.useMemo(() => {
    let list = allIdeas.filter(i => selectedSet.has(i.id));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(i => i.title.toLowerCase().includes(q) || (i.cat || '').toLowerCase().includes(q) || (i.area || '').toLowerCase().includes(q));
    }
    if (isMultiCity) {
      if (filterCity) list = list.filter(i => (i.destCity || 'Untagged') === filterCity);
    } else if (filterArea) {
      list = list.filter(i => i.area === filterArea);
    }
    if (sortBy === 'scheduled') list = [...list].sort((a, b) => (overflowIds.has(a.id) ? 1 : 0) - (overflowIds.has(b.id) ? 1 : 0));
    else if (sortBy === 'unscheduled') list = [...list].sort((a, b) => (overflowIds.has(b.id) ? 1 : 0) - (overflowIds.has(a.id) ? 1 : 0));
    else if (sortBy === 'cat') list = [...list].sort((a, b) => (a.cat || '').localeCompare(b.cat || '') || a.title.localeCompare(b.title));
    else if (sortBy === 'dur') list = [...list].sort((a, b) => b.dur - a.dur);
    else if (sortBy === 'az') list = [...list].sort((a, b) => a.title.localeCompare(b.title));
    return list;
  }, [allIdeas, selectedSet, search, sortBy, filterArea, filterCity, isMultiCity, overflowIds]); // eslint-disable-line

  // Excludes ideas already placed on the itinerary even when unselected —
  // an item added straight to a day (see ItineraryScreen#addCustomIdea)
  // never joins selectedIds, so without this it would wrongly resurface
  // here as "available to add" even though it's already on the calendar.
  const unselectedIdeas = allIdeas.filter(i => !selectedSet.has(i.id) && !placedIdeaIds.has(i.id));
  const activeSortLabel = IDEA_SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Sort';

  // Grouped in trip (leg) order, not alphabetical — matches how the cities
  // actually come up on the itinerary. Anything without a destCity (an
  // untagged custom idea) falls into a trailing "Untagged" group instead of
  // silently vanishing or being guessed into the wrong city.
  const cityGroups = React.useMemo(() => {
    if (!isMultiCity) return null;
    const legOrder = [...new Set(legs.map(l => (l.destination || '').split(',')[0].trim()))];
    const byCity = {};
    myIdeas.forEach(it => {
      const city = it.destCity || 'Untagged';
      (byCity[city] = byCity[city] || []).push(it);
    });
    const orderedCities = [...legOrder.filter(c => byCity[c]), ...Object.keys(byCity).filter(c => !legOrder.includes(c))];
    return orderedCities.map(city => ({ city, items: byCity[city] }));
  }, [isMultiCity, myIdeas, legs]);

  const renderIdeaRow = (it) => {
    const isCustom = customIdeaIds.has(it.id);
    const inOverflow = overflowIds.has(it.id);
    if (isCustom && editingId === it.id) {
      return (
        <div key={it.id} style={{ marginBottom: 10 }}>
          <IdeaEditForm idea={it} accent={accent} legs={legs}
            onSave={(u) => { onEditCustom?.(u); setEditingId(null); }}
            onCancel={() => setEditingId(null)} />
        </div>
      );
    }
    const isExpanded = expandedId === it.id;
    const hasDetails = !!(it.sourceUrl || it.meta || it.area);
    return (
      <React.Fragment key={it.id}>
        <div
          onClick={() => hasDetails && setExpandedId(isExpanded ? null : it.id)}
          onDoubleClick={() => isCustom && setEditingId(it.id)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: isExpanded ? 'none' : `0.5px solid ${PALETTE.ruleSoft}`, cursor: hasDetails ? 'pointer' : 'default' }}
        >
          <div style={{ width: 52, height: 44, borderRadius: 8, flexShrink: 0, background: SWATCH_BY_CAT[it.cat] || PALETTE.paperDim, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 9px)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Fraunces', fontSize: 15, color: PALETTE.ink, lineHeight: 1.2, letterSpacing: -0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                {it.cat} · {it.dur < 1 ? `${Math.round(it.dur * 60)}m` : `${it.dur}h`}
              </span>
              {inOverflow ? (
                <span style={{ padding: '1px 5px', borderRadius: 3, background: PALETTE.paperDim, color: PALETTE.inkMute, fontFamily: 'JetBrains Mono', fontSize: 8, letterSpacing: 0.4, textTransform: 'uppercase' }}>Unscheduled</span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '1px 5px', borderRadius: 3, background: `${accent}18`, color: accent, fontFamily: 'JetBrains Mono', fontSize: 8, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  <Icon name="check" size={8} stroke={2.5} />Scheduled
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0, alignItems: 'center' }}>
            {hasDetails && (
              <Icon name={isExpanded ? 'arrowUp' : 'arrowDn'} size={11} style={{ color: PALETTE.inkMute, marginRight: 2 }} />
            )}
            {isCustom && (
              <button onClick={(e) => { e.stopPropagation(); setEditingId(it.id); }} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: 5, borderRadius: 6, display: 'flex', alignItems: 'center' }}>
                <Icon name="pencil" size={13} />
              </button>
            )}
            <button onClick={(e) => { e.stopPropagation(); isCustom ? onDeleteCustom?.(it.id) : toggle(it.id); }} title="Remove" style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: 5, borderRadius: 6, display: 'flex', alignItems: 'center' }}>
              <Icon name="x" size={13} />
            </button>
          </div>
        </div>
        {isExpanded && (
          <div style={{ padding: '2px 0 12px 62px', display: 'flex', flexDirection: 'column', gap: 6, borderBottom: `0.5px solid ${PALETTE.ruleSoft}` }}>
            {it.area && (
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>📍 {it.area}</div>
            )}
            {it.meta && (
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, lineHeight: 1.5 }}>{it.meta}</div>
            )}
            {isSafeHttpUrl(it.sourceUrl) && (
              <a href={it.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.accent, textDecoration: 'none', wordBreak: 'break-all' }}>
                🔗 {it.sourceUrl}
              </a>
            )}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div style={{ padding: `12px 0 100px` }}>
      {/* Filter chips — the trip's own cities for multi-city trips (never
          raw neighborhood names, which don't map to the trip's actual
          legs), area/neighborhood chips for single-city trips. Leads the
          tab so picking a city here can drive the auto-tag on IdeaBar
          below it, instead of being buried under the add/search bar. */}
      {isMultiCity ? (
        cityOptions.length > 1 && (
          <div style={{ padding: `0 0 10px`, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
            <div style={{ display: 'flex', gap: 6, padding: `0 ${px}px`, width: 'max-content' }}>
              <button
                onClick={() => setFilterCity(null)}
                style={{ appearance: 'none', cursor: 'pointer', padding: '4px 11px', borderRadius: 20, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', border: filterCity === null ? 'none' : `0.5px solid ${PALETTE.rule}`, background: filterCity === null ? PALETTE.ink : PALETTE.card, color: filterCity === null ? PALETTE.paper : PALETTE.inkSoft, transition: 'all 0.12s' }}
              >
                All cities
              </button>
              {cityOptions.map(city => (
                <button
                  key={city}
                  onClick={() => setFilterCity(filterCity === city ? null : city)}
                  style={{ appearance: 'none', cursor: 'pointer', padding: '4px 11px', borderRadius: 20, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', border: filterCity === city ? 'none' : `0.5px solid ${PALETTE.rule}`, background: filterCity === city ? accent : PALETTE.card, color: filterCity === city ? '#fff' : PALETTE.inkSoft, transition: 'all 0.12s' }}
                >
                  {city}
                </button>
              ))}
            </div>
          </div>
        )
      ) : (
        areas.length > 1 && (
          <div style={{ padding: `0 0 10px`, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
            <div style={{ display: 'flex', gap: 6, padding: `0 ${px}px`, width: 'max-content' }}>
              <button
                onClick={() => setFilterArea(null)}
                style={{ appearance: 'none', cursor: 'pointer', padding: '4px 11px', borderRadius: 20, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', border: filterArea === null ? 'none' : `0.5px solid ${PALETTE.rule}`, background: filterArea === null ? PALETTE.ink : PALETTE.card, color: filterArea === null ? PALETTE.paper : PALETTE.inkSoft, transition: 'all 0.12s' }}
              >
                All areas
              </button>
              {areas.map(area => (
                <button
                  key={area}
                  onClick={() => setFilterArea(filterArea === area ? null : area)}
                  style={{ appearance: 'none', cursor: 'pointer', padding: '4px 11px', borderRadius: 20, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', border: filterArea === area ? 'none' : `0.5px solid ${PALETTE.rule}`, background: filterArea === area ? accent : PALETTE.card, color: filterArea === area ? '#fff' : PALETTE.inkSoft, transition: 'all 0.12s' }}
                >
                  {area}
                </button>
              ))}
            </div>
          </div>
        )
      )}

      {/* IdeaBar — doubles as the search box for "My ideas" below (onQueryChange
          drives `search`), so there's one bar instead of a search bar plus a
          separate add bar. "Get inspiration" sits beside it as a small icon
          instead of its own full-width banner. */}
      <div style={{ padding: `0 ${px}px 12px`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <IdeaBar onAdd={onAddCustom} accent={accent} legs={legs} onQueryChange={setSearch} defaultCityIdx={defaultCityIdx} />
        </div>
        <button
          onClick={() => setShowInspiration(true)}
          title={`Get inspiration${unselectedIdeas.length > 0 ? ` · ${unselectedIdeas.length} available` : ''}`}
          aria-label="Get inspiration"
          style={{ position: 'relative', appearance: 'none', cursor: 'pointer', flexShrink: 0, width: 42, height: 42, borderRadius: 12, background: 'transparent', border: `1.5px solid ${PALETTE.rule}`, color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', touchAction: 'manipulation' }}
        >
          <Icon name="sparkle" size={17} />
          {unselectedIdeas.length > 0 && (
            <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 3px', borderRadius: 999, background: accent, color: '#fff', fontFamily: 'JetBrains Mono', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {unselectedIdeas.length}
            </span>
          )}
        </button>
      </div>

      {/* Section heading — count + sort (the sort dropdown used to sit next
          to a standalone search bar; that bar merged into the idea bar
          above, so sort moved down here). */}
      <div style={{ padding: `0 ${px}px 6px`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 1.2, textTransform: 'uppercase' }}>My ideas</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
            {myIdeas.length}{(search || filterArea || filterCity) ? ` of ${allIdeas.filter(i => selectedSet.has(i.id)).length}` : ''} · {myIdeas.reduce((s, i) => s + i.dur, 0).toFixed(1)}h
          </div>
          <div ref={sortRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowSort(s => !s)}
              style={{ appearance: 'none', border: `1px solid ${showSort ? PALETTE.ink : PALETTE.rule}`, background: showSort ? PALETTE.ink : PALETTE.card, color: showSort ? PALETTE.paper : PALETTE.inkSoft, borderRadius: 8, padding: '5px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap' }}
            >
              <Icon name="list" size={11} />
              {activeSortLabel}
            </button>
            {showSort && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 50, background: PALETTE.card, borderRadius: 10, border: `1px solid ${PALETTE.rule}`, boxShadow: '0 8px 24px rgba(34,31,26,0.12)', overflow: 'hidden', minWidth: 160 }}>
                {IDEA_SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', appearance: 'none', border: 0, background: sortBy === opt.value ? PALETTE.paperDim : 'transparent', color: sortBy === opt.value ? PALETTE.ink : PALETTE.inkSoft, cursor: 'pointer', padding: '9px 14px', fontFamily: 'Inter', fontSize: 12, fontWeight: sortBy === opt.value ? 600 : 400, textAlign: 'left' }}
                  >
                    {opt.label}
                    {sortBy === opt.value && <Icon name="check" size={12} stroke={2.5} style={{ color: accent }} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ideas list — grouped by city for multi-city trips so "which city is
          this for" is a section heading, not a tiny inline tag you have to
          hunt for on every row. */}
      <div style={{ padding: `0 ${px}px` }}>
        {myIdeas.length === 0 ? (
          <div style={{ padding: '16px 0 4px' }}>
            <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, lineHeight: 1.6 }}>
              {search ? 'No ideas match your search.' : 'No ideas added yet. Use the field above to add your own, or get inspiration below.'}
            </div>
          </div>
        ) : isMultiCity ? (
          cityGroups.map(({ city, items }) => (
            <div key={city}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: accent, padding: '10px 0 4px' }}>
                {city} · {items.length}
              </div>
              {items.map(it => renderIdeaRow(it))}
            </div>
          ))
        ) : myIdeas.map(it => renderIdeaRow(it))}
      </div>

      {showInspiration && (
        <InspirationSheet
          ideas={unselectedIdeas}
          accent={accent}
          onAdd={(id) => toggle(id)}
          onAddCustom={onAddCustom}
          onClose={() => setShowInspiration(false)}
          legs={legs}
          bookings={bookings}
          destination={destination}
        />
      )}
    </div>
  );
}


export function PresetsPanel({ selectedPresets, setSelectedPresets, destIdeas, allIdeas, selectedIds, setSelectedIds, localPlan, setLocalPlan, onSavePlan, dayCount, displayDays, trip, accent, px, legs = null }) {
  const [expanded, setExpanded] = React.useState(null);
  const [applyFlash, setApplyFlash] = React.useState(null);

  // Multi-city trips: split presets per leg, each offset to that leg's own
  // day range so "Insert at Day N" stays inside the right city's block.
  const sections = React.useMemo(() => {
    if (legs && legs.length > 1) {
      const { offsets, cardCounts } = computeLegSchedule(legs);
      return legs.map((leg, i) => ({
        city: (leg.destination || '').split(',')[0].trim(),
        presets: getPresetsForDest(leg.destination || ''),
        offset: offsets[i],
        dayCount: cardCounts[i],
      }));
    }
    return [{ city: null, presets: getPresetsForDest(trip?.destination || ''), offset: 0, dayCount }];
  }, [legs, trip?.destination, dayCount]);

  const ideaMap = React.useMemo(() => {
    const m = {};
    allIdeas.forEach(i => { m[i.id] = i; });
    return m;
  }, [allIdeas]);

  const handleApplyPreset = (preset, startDay) => {
    const allPresetIds = preset.days.flatMap(d => d.ideas);

    // Add preset ideas to selectedIds
    setSelectedIds(prev => [...new Set([...prev, ...allPresetIds])]);

    // Build plan: freeze current display if needed
    const basePlan = localPlan
      ? localPlan.map(d => [...d])
      : displayDays.map(day => day.items.map(slot => slot.idea.id));

    // Ensure plan has enough rows
    while (basePlan.length < dayCount) basePlan.push([]);

    // Merge preset days into target days
    preset.days.forEach((presetDay, i) => {
      const targetIdx = startDay + i;
      if (targetIdx < basePlan.length) {
        const existing = new Set(basePlan[targetIdx]);
        presetDay.ideas.forEach(id => { if (!existing.has(id)) basePlan[targetIdx].push(id); });
      }
    });

    setLocalPlan(basePlan);
    onSavePlan?.(basePlan);
    setApplyFlash(preset.id);
    setTimeout(() => setApplyFlash(null), 1800);
  };

  return (
    <div style={{ padding: `16px ${px}px 100px` }}>
      <div style={{ fontFamily: 'Fraunces', fontSize: 26, letterSpacing: -0.5, color: PALETTE.ink, marginBottom: 4 }}>Day presets</div>
      <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkSoft, marginBottom: 16, lineHeight: 1.5 }}>
        Pick a preset and insert it as a block into any starting day of your trip.
      </div>
      {sections.every(s => s.presets.length === 0) ? (
        <div style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute, textAlign: 'center', padding: '40px 0' }}>
          No presets available for this destination yet.
        </div>
      ) : (
        sections.map(section => (
          <div key={section.city || 'single'} style={{ marginBottom: 22 }}>
            {section.city && (
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
                {section.city}
              </div>
            )}
            {section.presets.length === 0 ? (
              <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, padding: '8px 0' }}>
                No presets available for {section.city || 'this destination'} yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {section.presets.map(p => (
                  <ItinPresetCard
                    key={p.id}
                    preset={p}
                    expanded={expanded === p.id}
                    onExpand={() => setExpanded(expanded === p.id ? null : p.id)}
                    onApply={(preset, startDay) => handleApplyPreset(preset, section.offset + startDay)}
                    applyFlash={applyFlash === p.id}
                    dayCount={section.dayCount}
                    dayOffset={section.offset}
                    accent={accent}
                    ideaMap={ideaMap}
                  />
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function ItinPresetCard({ preset, expanded, onExpand, onApply, applyFlash, dayCount, accent, ideaMap, dayOffset = 0 }) {
  const [startDay, setStartDay] = React.useState(0);
  const totalActivities = preset.days.flatMap(d => d.ideas).length;
  const maxStart = Math.max(0, dayCount - preset.days.length);

  return (
    <div style={{
      background: PALETTE.card, borderRadius: 14, overflow: 'hidden',
      border: `0.5px solid ${PALETTE.ruleSoft}`,
      transition: 'border 0.15s',
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <Patch swatch={preset.swatch} height={100} style={{ width: 80, flexShrink: 0, borderRadius: 0 }} />
        <div style={{ flex: 1, padding: '12px 12px 12px 14px', position: 'relative', minWidth: 0 }}>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 1.2, textTransform: 'uppercase' }}>
            {preset.kicker}
          </div>
          <div style={{ fontFamily: 'Fraunces', fontSize: 17, letterSpacing: -0.3, color: PALETTE.ink, marginTop: 3, lineHeight: 1.2 }}>
            {preset.title}
          </div>
          <div style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft, marginTop: 4, lineHeight: 1.4 }}>
            {preset.body}
          </div>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5 }}>
              {preset.days.length} day{preset.days.length > 1 ? 's' : ''} · {totalActivities} activities
            </span>
            <button
              onClick={onExpand}
              style={{ appearance: 'none', border: 'none', background: 'transparent', padding: '2px 0', cursor: 'pointer', fontFamily: 'Inter', fontSize: 11, color: accent, display: 'flex', alignItems: 'center', gap: 3 }}
            >
              {expanded ? 'Hide' : 'Preview'}
              <Icon name={expanded ? 'arrowUp' : 'arrowDn'} size={11} />
            </button>
          </div>
        </div>
      </div>

      {/* Expanded: day breakdown + apply controls */}
      {expanded && (
        <div style={{ borderTop: `0.5px solid ${PALETTE.ruleSoft}`, padding: '12px 14px' }}>
          {preset.days.map((day, i) => (
            <div key={i} style={{ marginBottom: i < preset.days.length - 1 ? 10 : 12 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 4 }}>
                Day {i + 1} — {day.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {day.ideas.map(id => {
                  const idea = ideaMap[id];
                  if (!idea) return null;
                  return (
                    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 4, height: 4, borderRadius: 2, background: PALETTE.inkMute, flexShrink: 0, marginTop: 1 }} />
                      <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>{idea.title}</span>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, marginLeft: 'auto', flexShrink: 0 }}>
                        {idea.meta?.split('·')[1]?.trim() || ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Start day picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute, letterSpacing: 0.5, textTransform: 'uppercase', flexShrink: 0 }}>
              Insert at:
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Array.from({ length: maxStart + 1 }, (_, i) => (
                <button key={i} onClick={() => setStartDay(i)} style={{
                  appearance: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: 20,
                  fontFamily: 'JetBrains Mono', fontSize: 11,
                  background: startDay === i ? accent : PALETTE.paperDim,
                  color: startDay === i ? '#fff' : PALETTE.inkSoft,
                  border: startDay === i ? 'none' : `0.5px solid ${PALETTE.ruleSoft}`,
                }}>
                  Day {dayOffset + i + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Apply button */}
          <button onClick={() => onApply(preset, startDay)} style={{
            appearance: 'none', cursor: 'pointer', width: '100%',
            padding: '10px 0', borderRadius: 10, border: 'none',
            background: applyFlash ? PALETTE.green || '#3a8a3a' : accent,
            color: '#fff', fontFamily: 'Inter', fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'background 0.2s',
          }}>
            {applyFlash ? <><Icon name="check" size={15} /> Applied!</> : <>Add to itinerary</>}
          </button>
        </div>
      )}
    </div>
  );
}

export function DatesPanel({ trip, onSaveDates, onSaveLegs, accent, px }) {
  const legs = trip?.legs && trip.legs.length > 1 ? trip.legs : null;
  const [dateStart, setDateStartLocal] = React.useState(trip?.dateStart || '');
  const [dateEnd, setDateEnd] = React.useState(trip?.dateEnd || '');
  const [travelers, setTravelers] = React.useState(trip?.travelers || 2);
  const [stops, setStopsLocal] = React.useState(() => (
    legs ? legs.map((l, i) => ({ id: `leg_${i}`, destination: l.destination || '', dayCount: l.dayCount || 1, overlapStart: !!l.overlapStart })) : []
  ));
  const [flash, setFlash] = React.useState('');
  const [returnTrigger, setReturnTrigger] = React.useState(0);
  const saveLegsTimeout = React.useRef(null);
  const today = new Date().toISOString().split('T')[0];

  // Re-sync local state whenever a different trip is opened.
  React.useEffect(() => {
    setDateStartLocal(trip?.dateStart || '');
    setDateEnd(trip?.dateEnd || '');
    setTravelers(trip?.travelers || 2);
    setStopsLocal(trip?.legs && trip.legs.length > 1
      ? trip.legs.map((l, i) => ({ id: `leg_${i}`, destination: l.destination || '', dayCount: l.dayCount || 1, overlapStart: !!l.overlapStart }))
      : []);
  }, [trip?.id]); // eslint-disable-line

  const showFlash = () => { setFlash('Saved'); setTimeout(() => setFlash(''), 1400); };

  const doSaveDates = React.useCallback((ds, de, tr) => {
    onSaveDates?.({ dateStart: ds, dateEnd: de, travelers: tr });
    showFlash();
  }, [onSaveDates]);

  const setTravelersAndSave = (t) => { setTravelers(t); doSaveDates(dateStart, dateEnd, t); };

  // Multi-city trips get the SAME route-builder used at trip creation, fully
  // editable here — add/remove cities, rename, resize. City edits debounce
  // to onSaveLegs (avoids a PATCH per keystroke); date-window edits go
  // through onSaveDates with a dateEnd recomputed from the current stops so
  // it never drifts from what the cities actually add up to.
  const setStops = (updater) => {
    setStopsLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const cleanStops = next.filter(s => s.destination.trim()).map(({ destination, dayCount, overlapStart }) => ({ destination, dayCount, overlapStart: !!overlapStart }));
      if (cleanStops.length > 1) {
        const { totalDays } = computeLegSchedule(cleanStops);
        setDateEnd(dateStart ? addDaysIso(dateStart, totalDays - 1) : '');
        clearTimeout(saveLegsTimeout.current);
        saveLegsTimeout.current = setTimeout(() => {
          onSaveLegs?.(cleanStops);
          showFlash();
        }, 500);
      }
      return next;
    });
  };

  const setMultiDateStart = (v) => {
    setDateStartLocal(v);
    const { totalDays } = computeLegSchedule(stops);
    const de = v ? addDaysIso(v, totalDays - 1) : '';
    setDateEnd(de);
    doSaveDates(v, de, travelers);
  };

  return (
    <div style={{ padding: `20px ${px}px 40px`, display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 22, color: PALETTE.ink }}>
          {legs
            ? legs.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ')
            : (trip?.destination || '—')}
        </div>
        {flash && <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.green, letterSpacing: 0.8 }}>{flash} ✓</div>}
      </div>

      {legs ? (
        <MultiCityStops stops={stops} setStops={setStops} dateStart={dateStart} setDateStart={setMultiDateStart} />
      ) : (
        <div style={{ display: 'flex', gap: 16 }}>
          <CalPicker
            label="Depart"
            value={dateStart}
            min={today}
            onChange={v => {
              const de = dateEnd && v > dateEnd ? '' : dateEnd;
              setDateStartLocal(v); setDateEnd(de);
              doSaveDates(v, de, travelers);
              if (v) setReturnTrigger(t => t + 1);
            }}
          />
          <CalPicker
            label="Return"
            value={dateEnd}
            min={dateStart || today}
            onChange={v => { setDateEnd(v); doSaveDates(dateStart, v, travelers); }}
            openTrigger={returnTrigger}
          />
        </div>
      )}

      <div>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: PALETTE.inkMute, marginBottom: 10 }}>Travelers</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={() => setTravelersAndSave(Math.max(1, travelers - 1))} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, width: 32, height: 32, background: PALETTE.card, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: PALETTE.ink, fontSize: 18 }}>−</button>
          <span style={{ fontFamily: 'Fraunces', fontSize: 22, color: PALETTE.ink, minWidth: 20, textAlign: 'center' }}>{travelers}</span>
          <button onClick={() => setTravelersAndSave(travelers + 1)} style={{ appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 8, width: 32, height: 32, background: PALETTE.card, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: PALETTE.ink, fontSize: 18 }}>+</button>
          <span style={{ fontFamily: 'Inter', fontSize: 13, color: PALETTE.inkMute }}>{travelers === 1 ? 'solo' : travelers === 2 ? 'you + 1' : `${travelers} people`}</span>
        </div>
      </div>
    </div>
  );
}

